import type { FeedbackCapture, FeedbackReportSubmission } from "../../shared/api/types";
import { BUILD_SHA, CONTRACT_VERSION } from "./buildInfo";
import type { ElementInfo, PinPoint, ScreenshotInfo } from "./capture";
import type { ApiCallEntry, ConsoleEntry } from "./diagnostics";
import { redactQuery, redactUrl, redactValue } from "./redact";

export type FeedbackKindChoice = FeedbackReportSubmission["kind"];

export interface PageFacts {
  url: string;
  route?: string;
  routeParams: Record<string, string>;
  query: string;
  title: string;
  screenId?: string;
  screenTitle?: string;
}

export interface CaptureInput {
  page: PageFacts;
  pin: PinPoint;
  elementRect?: { left: number; top: number };
  element: ElementInfo;
  viewing: { projectKey?: string; itemRef?: string; state?: Record<string, unknown> };
  flagsOn: string[];
  screenshot: ScreenshotInfo;
  console: ConsoleEntry[];
  apiCalls: ApiCallEntry[];
}

/** The server takes at most 1 MiB for the report part; stay well under it by dropping the oldest log lines first. */
export const REPORT_BUDGET_BYTES = 900 * 1024;

function timeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
}

/** The capture as the contract's FeedbackCapture: redacted, bounded, and small enough to send. */
export function buildCapture(input: CaptureInput): FeedbackCapture {
  const { pin, elementRect } = input;
  const capture = {
    page: {
      url: redactUrl(input.page.url),
      route: input.page.route,
      routeParams: input.page.routeParams,
      query: redactQuery(input.page.query),
      title: input.page.title,
      screenId: input.page.screenId,
      screenTitle: input.page.screenTitle,
    },
    pin: {
      viewportX: Math.round(pin.x),
      viewportY: Math.round(pin.y),
      elementX: elementRect ? Math.round(pin.x - elementRect.left) : undefined,
      elementY: elementRect ? Math.round(pin.y - elementRect.top) : undefined,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      scrollX: Math.round(window.scrollX),
      scrollY: Math.round(window.scrollY),
      devicePixelRatio: window.devicePixelRatio || 1,
    },
    element: input.element,
    viewing: input.viewing,
    client: {
      userAgent: navigator.userAgent,
      language: navigator.language,
      timeZone: timeZone(),
      clientTime: new Date().toString(),
      buildSha: BUILD_SHA,
      contractVersion: CONTRACT_VERSION,
      flagsOn: input.flagsOn,
    },
    screenshot: input.screenshot,
    console: input.console,
    apiCalls: input.apiCalls,
  };
  let bounded = redactValue(capture) as FeedbackCapture & { console: unknown[]; apiCalls: unknown[] };
  // Oldest lines go first until it fits (a report must never fail for being too big).
  while (new Blob([JSON.stringify(bounded)]).size > REPORT_BUDGET_BYTES && (bounded.console.length > 0 || bounded.apiCalls.length > 0)) {
    bounded = {
      ...bounded,
      console: bounded.console.slice(Math.ceil(bounded.console.length / 2)),
      apiCalls: bounded.apiCalls.slice(Math.ceil(bounded.apiCalls.length / 2)),
    };
  }
  return bounded;
}
